import {
  type CohortBuilderDefinitionLegacy,
  CohortBuilderItemChangedBy,
  CohortBuilderItemChangeStatusLegacy,
  IndexDateTypeLegacy,
  type IndexDateVariableLegacy,
  type SimpleEventLegacy,
} from '@verantos-dev/types-cohorts';

import { computeDiffForCohortBuilderItem } from '../computeDiffForCohortBuilderItem';
import { getNextDerivedEventsForSimpleEventNameChange } from '../getNextDerivedEventsForSimpleEventNameChange';
import { getNextIndexDateForEventNameChange } from '../getNextIndexDateForEventNameChange';
import { getNextSubjectVariablesForSimpleEventNameChange } from '../getNextSubjectVariablesForSimpleEventNameChange';

export const getFullCohortDslForSimpleEvent = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextSimpleEvent: SimpleEventLegacy,
  addChangeStatus = false
) => {
  const currentSimpleEvents = currentCohortDefinition?.simpleEvents ?? [];
  const currentDerivedEvents = currentCohortDefinition?.derivedEvents ?? [];
  const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
  const currentIndexDate = currentCohortDefinition?.indexDate ?? undefined;
  const payloadSimpleEventIndex = currentSimpleEvents.findIndex(
    (simpleEvent) => simpleEvent.id === nextSimpleEvent.id
  );

  // check if the name of the simple event has changed, to determine if we need to update the usages of this name
  // in items that reference this simple event.
  const isSimpleEventNameChanged =
    payloadSimpleEventIndex > -1 &&
    currentSimpleEvents[payloadSimpleEventIndex].name !== nextSimpleEvent.name;

  const nextDerivedEvents = isSimpleEventNameChanged
    ? getNextDerivedEventsForSimpleEventNameChange(
        currentDerivedEvents,
        currentSimpleEvents[payloadSimpleEventIndex].name,
        nextSimpleEvent.name,
        nextSimpleEvent.type
      )
    : currentDerivedEvents;
  const nextSubjectVariables = isSimpleEventNameChanged
    ? getNextSubjectVariablesForSimpleEventNameChange(
        currentSubjectVariables,
        currentSimpleEvents[payloadSimpleEventIndex].name,
        nextSimpleEvent.name,
        nextSimpleEvent.type
      )
    : currentSubjectVariables;

  let nextIndexDate: IndexDateVariableLegacy | undefined = currentIndexDate;
  if (
    isSimpleEventNameChanged &&
    currentIndexDate &&
    currentIndexDate.type === IndexDateTypeLegacy.EVENT
  ) {
    nextIndexDate = getNextIndexDateForEventNameChange(
      currentIndexDate,
      currentSimpleEvents[payloadSimpleEventIndex].name,
      nextSimpleEvent.name,
      nextSimpleEvent.type,
      true
    );
  }

  // If the simple event is already defined in state, replace it with the new one
  // Otherwise, add the new simple event to the end of the array
  const simpleEvents =
    payloadSimpleEventIndex > -1
      ? currentSimpleEvents.map((simpleEvent) => {
          if (simpleEvent.id === nextSimpleEvent.id) {
            if (addChangeStatus) {
              let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
              if (computeDiffForCohortBuilderItem(simpleEvent, nextSimpleEvent)) {
                // if simple event previously existed and is different from current update
                if (simpleEvent.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
                  // if simple event was new, then it's still new
                  _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
                } else {
                  // simple event is edited
                  _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
                }
              } else {
                // if simple event previously existed and is the same as current update, then it's unchanged
                _changeStatus = simpleEvent.changeStatus;
              }

              return {
                ...nextSimpleEvent,
                changeStatus: _changeStatus,
                changedBy: CohortBuilderItemChangedBy.COHORT_BUILDER,
              };
            } else {
              return nextSimpleEvent;
            }
          }
          return simpleEvent;
        })
      : [
          ...currentSimpleEvents,
          addChangeStatus
            ? {
                ...nextSimpleEvent,
                changeStatus: CohortBuilderItemChangeStatusLegacy.NEW,
                changedBy: CohortBuilderItemChangedBy.COHORT_BUILDER,
              }
            : nextSimpleEvent,
        ];

  const cohortDefinition: CohortBuilderDefinitionLegacy = {
    studyPeriodRestrictions: currentCohortDefinition?.studyPeriodRestrictions,
    subjectVariables: nextSubjectVariables,
    indexDate: nextIndexDate,
    simpleEvents,
    derivedEvents: nextDerivedEvents,
    // FEATURE FLAG: Preserve analytical variables fields when present
    // These fields come from backend when analytical_variables_in_cohort_builder is enabled
    // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
    analyticalVariables: currentCohortDefinition?.analyticalVariables,
    criteria: currentCohortDefinition?.criteria,
  };

  return {
    cohortDefinition,
  };
};

export const getFullCohortDslForDeleteSimpleEvent = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  simpleEventId: string
) => {
  const currentSimpleEvents = currentCohortDefinition?.simpleEvents ?? [];
  const payloadSimpleEventIndex = currentSimpleEvents.findIndex(
    (simpleEvent) => simpleEvent.id === simpleEventId
  );

  const cohortDefinition: CohortBuilderDefinitionLegacy = {
    studyPeriodRestrictions: currentCohortDefinition?.studyPeriodRestrictions,
    indexDate: currentCohortDefinition?.indexDate,
    subjectVariables: currentCohortDefinition?.subjectVariables ?? [],
    simpleEvents: [
      ...currentSimpleEvents.slice(0, payloadSimpleEventIndex),
      ...currentSimpleEvents.slice(payloadSimpleEventIndex + 1),
    ],
    derivedEvents: currentCohortDefinition?.derivedEvents ?? [],
    // FEATURE FLAG: Preserve analytical variables fields when present
    // These fields come from backend when analytical_variables_in_cohort_builder is enabled
    // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
    analyticalVariables: currentCohortDefinition?.analyticalVariables,
    criteria: currentCohortDefinition?.criteria,
  };

  return {
    cohortDefinition,
  };
};
